import React, { useState, useEffect } from 'react';
import { ShoppingBag, Menu, X } from 'lucide-react';
import { useCart } from '../context/CartContext';
import { DesktopLinks, MobileMenu } from './NavbarMenus';
import { SearchBar } from './SearchBar';
import { useCategories } from '../hooks/useCategories';

const Navbar = ({ onNavigate }) => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [isCollectionsOpen, setIsCollectionsOpen] = useState(false);
  const { getCartCount, toggleCart } = useCart();
  const categories = useCategories();
  const collections = [
    ...categories.map((c) => ({ id: c.slug, label: c.name })),
    { id: 'new-arrivals', label: 'New Arrivals' },
    { id: 'best-sellers', label: 'Best Sellers' },
  ];

  useEffect(() => {
    let ticking = false;
    const handleScroll = () => {
      if (ticking) return;
      ticking = true;
      window.requestAnimationFrame(() => {
        setIsScrolled(window.scrollY > 20);
        ticking = false;
      });
    };
    handleScroll();
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleNavigate = (section) => {
    onNavigate(section);
    setIsMenuOpen(false);
    setIsCollectionsOpen(false);
  };

  const textColor = isScrolled ? 'text-[#001B2A]' : 'text-white';
  const taglineColor = isScrolled ? 'text-[#7B1E2B]' : 'text-[#E6D7B3]';

  return (
    <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
      isScrolled ? 'bg-white/95 backdrop-blur-md shadow-md' : 'bg-transparent'
    }`}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16 md:h-20">
          {/* Logo */}
          <div className="flex-shrink-0 cursor-pointer flex items-center gap-2.5" onClick={() => handleNavigate('home')}>
            <img src="/bowtie-logo.png" alt="The Bow Tie Guy logo" className="h-8 w-8 md:h-10 md:w-10 object-contain" data-testid="navbar-logo" />
            <div>
              <h1 className={`text-xl md:text-2xl font-serif font-bold tracking-tight ${textColor} transition-colors hover:text-[#7B1E2B] leading-none`}>
                THE BOW TIE GUY
              </h1>
              <p className={`text-[9px] md:text-[10px] tracking-[0.35em] ${taglineColor} font-semibold mt-0.5`}>MENS FASHION</p>
            </div>
          </div>

          <DesktopLinks
            onNavigate={handleNavigate}
            isCollectionsOpen={isCollectionsOpen}
            setIsCollectionsOpen={setIsCollectionsOpen}
            linkClass={textColor}
            collections={collections}
          />

          {/* Cart & Mobile Menu */}
          <div className="flex items-center space-x-4">
            <SearchBar textColor={textColor} />
            <button
              onClick={toggleCart}
              data-testid="navbar-cart-button"
              className={`relative p-2 ${textColor} hover:text-[#7B1E2B] transition-colors`}
            >
              <ShoppingBag className="h-5 w-5 md:h-6 md:w-6" />
              {getCartCount() > 0 && (
                <span data-testid="cart-count-badge" className="absolute -top-1 -right-1 bg-[#7B1E2B] text-white text-xs rounded-full h-5 w-5 flex items-center justify-center font-medium">
                  {getCartCount()}
                </span>
              )}
            </button>

            <button
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              className="md:hidden p-2 text-[#001B2A] hover:text-[#7B1E2B] transition-colors"
            >
              {isMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
            </button>
          </div>
        </div>
      </div>

      {isMenuOpen && <MobileMenu onNavigate={handleNavigate} collections={collections} />}
    </nav>
  );
};

export default Navbar;
